import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Bank, ShieldCheck, Warning, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * AgentBankInfoWall — modal-style hard wall rendered ABOVE the agent
 * portal whenever the logged-in agent has not yet provided commission
 * payout bank info. The wall cannot be dismissed (no X icon, no backdrop
 * click) until the agent submits valid info — that's the entire point of
 * the wall.
 *
 * After submit, calls `onComplete` so the parent can re-mount its real
 * dashboard content. The wall self-fetches its own initial state on
 * mount and renders nothing while loading so the portal doesn't flicker
 * between "ready" and "blocked".
 *
 * Modes (via `mode` prop):
 *   - "auto" (default): Self-fetch status on mount. Block until complete.
 *     Cannot be dismissed.
 *   - "edit": Always opens, regardless of completeness. CAN be dismissed
 *     via Escape / backdrop / explicit Cancel. Pre-fills holder/bank
 *     name from existing data so agents only re-type the routing &
 *     account if they want to change them. Used by the "Edit payout
 *     bank" header link.
 */
export default function AgentBankInfoWall({ onComplete, mode = "auto", onDismiss }) {
  const isEdit = mode === "edit";
  const [state, setState] = useState(isEdit ? "loading-edit" : "loading"); // loading | loading-edit | needed | ok
  const [form, setForm] = useState({
    bank_account_holder: "", bank_name: "",
    routing_number: "", bank_account_number: "", confirm_account: "",
    swift_code: "", iban: "",
  });
  // On-file masked preview, set from GET so the edit form can display
  // "Currently on file: •••• 7890" hints without echoing the full value.
  const [onFile, setOnFile] = useState({
    account_last4: "", routing_last4: "",
    iban_country: "", iban_last4: "",
  });
  const [busy, setBusy] = useState(false);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/agent/payout-bank");
        if (cancelled) return;
        // Edit mode: ALWAYS open the form, pre-filled with current values
        // (except the account-number fields which require re-typing for
        // safety since we don't echo them back).
        if (isEdit) {
          setForm(f => ({
            ...f,
            bank_account_holder: r.data?.bank_account_holder || "",
            bank_name: r.data?.bank_name || "",
            swift_code: r.data?.swift_code || "",
          }));
          setOnFile({
            account_last4: r.data?.account_last4 || "",
            routing_last4: r.data?.routing_last4 || "",
            iban_country: r.data?.iban_country || "",
            iban_last4: r.data?.iban_last4 || "",
          });
          setState("needed");
          return;
        }
        if (r.data?.complete) {
          setState("ok");
          onComplete?.();
        } else {
          // Prefill name & bank if any partial data already exists so the
          // agent only has to fill in what's actually missing.
          setForm(f => ({
            ...f,
            bank_account_holder: r.data?.bank_account_holder || "",
            bank_name: r.data?.bank_name || "",
            swift_code: r.data?.swift_code || "",
          }));
          setState("needed");
        }
      } catch (err) {
        // If the lookup fails (e.g. expired session), let the parent
        // render normally — the global auth interceptor will handle
        // re-login as needed.
        if (!cancelled) {
          setState("ok");
          onComplete?.();
        }
      }
    })();
    return () => { cancelled = true; };
  }, [onComplete, isEdit]);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const holder = form.bank_account_holder.trim();
    const bank = form.bank_name.trim();
    const routing = form.routing_number.replace(/[\s-]/g, "");
    const account = form.bank_account_number.replace(/[\s-]/g, "");
    const confirm = form.confirm_account.replace(/[\s-]/g, "");
    const swift = form.swift_code.replace(/\s/g, "").toUpperCase();
    const iban = form.iban.replace(/[\s-]/g, "").toUpperCase();

    if (!holder || !bank || !routing || !account) {
      setShake(true); setTimeout(() => setShake(false), 400);
      toast.error("Holder name, bank, routing, and account are required.");
      return;
    }
    if (account !== confirm) {
      setShake(true); setTimeout(() => setShake(false), 400);
      toast.error("Account number doesn't match the confirmation field.");
      return;
    }
    if (!/^[A-Za-z0-9]{4,20}$/.test(routing)) {
      setShake(true); setTimeout(() => setShake(false), 400);
      toast.error("Routing number must be 4-20 letters/digits (no symbols).");
      return;
    }
    if (!/^[A-Za-z0-9]{4,34}$/.test(account)) {
      setShake(true); setTimeout(() => setShake(false), 400);
      toast.error("Account number must be 4-34 letters/digits (no symbols).");
      return;
    }
    if (swift && !/^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(swift)) {
      setShake(true); setTimeout(() => setShake(false), 400);
      toast.error("SWIFT/BIC code must be 8 or 11 letters/digits (e.g. CHASUS33 or CHASUS33XXX).");
      return;
    }
    if (iban) {
      // ISO 13616 shape: 2 letters country + 2 digit check + 11-30 alnum BBAN.
      // Total length 15-34 chars.
      if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) {
        setShake(true); setTimeout(() => setShake(false), 400);
        toast.error("IBAN must be 15-34 chars: 2 country letters + 2 check digits + bank account (e.g. GB82WEST12345698765432).");
        return;
      }
    }

    setBusy(true);
    try {
      await api.post("/agent/payout-bank", {
        bank_account_holder: holder,
        bank_name: bank,
        routing_number: routing,
        bank_account_number: account,
        swift_code: swift,
        iban,
      });
      toast.success(isEdit ? "Payout bank info updated" : "Payout bank info saved");
      setState("ok");
      onComplete?.();
      if (isEdit) onDismiss?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save bank info");
    } finally {
      setBusy(false);
    }
  };

  if (state !== "needed") return null;

  return (
    <Dialog open onOpenChange={(v) => { if (!v && isEdit) onDismiss?.(); /* else hard wall — cannot be dismissed */ }}>
      <DialogContent
        // In auto-wall mode, hide the close X and block outside interaction;
        // in edit mode let users dismiss normally.
        className={`bg-[#FBF9F6] border-[#E5E0D8] max-w-lg ${isEdit ? "" : "[&>button.absolute]:hidden"} ${shake ? "animate-[shake_0.4s]" : ""}`}
        onPointerDownOutside={(e) => { if (!isEdit) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!isEdit) e.preventDefault(); }}
        onInteractOutside={(e) => { if (!isEdit) e.preventDefault(); }}
        data-testid="bank-info-wall"
      >
        <DialogHeader>
          <DialogTitle className="serif text-3xl text-[#184A30] flex items-center gap-2">
            <Bank size={28} weight="duotone" /> {isEdit ? "Edit payout bank" : "Commission payout setup"}
          </DialogTitle>
          <DialogDescription className="text-[#4A5C50]">
            {isEdit
              ? "Update the bank account where Zcardon will deposit your commission payouts. Account numbers are not echoed back — please re-type them to confirm changes."
              : "Before you can issue cards, we need the bank account where Zcardon will deposit your commission payouts. This is private and visible only to admins for payout processing."}
          </DialogDescription>
        </DialogHeader>

        {!isEdit && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 flex gap-2" data-testid="bank-info-wall-banner">
            <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
            <div>
              Commissions earn instantly when you issue cards but stay marked
              <span className="font-semibold"> unpaid</span> until we have your
              bank info on file. Save once — you can update later from your
              profile.
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3" data-testid="bank-info-wall-form">
          <div className="space-y-1.5">
            <Label htmlFor="b_holder" className="text-xs uppercase tracking-widest text-[#4A5C50]">
              Account holder name
            </Label>
            <Input
              id="b_holder" type="text" required
              value={form.bank_account_holder}
              onChange={update("bank_account_holder")}
              placeholder="As it appears on your bank statement"
              disabled={busy}
              data-testid="bank-info-holder-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="b_name" className="text-xs uppercase tracking-widest text-[#4A5C50]">
              Bank name
            </Label>
            <Input
              id="b_name" type="text" required
              value={form.bank_name}
              onChange={update("bank_name")}
              placeholder="Chase, Vietcombank, Wells Fargo…"
              disabled={busy}
              data-testid="bank-info-bank-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="b_route" className="text-xs uppercase tracking-widest text-[#4A5C50]">
              Routing number (US ACH)
            </Label>
            <Input
              id="b_route" type="text" required
              inputMode="numeric"
              autoComplete="off"
              value={form.routing_number}
              onChange={update("routing_number")}
              placeholder="9-digit US routing number"
              disabled={busy}
              data-testid="bank-info-routing-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="b_swift" className="text-xs uppercase tracking-widest text-[#4A5C50]">
              SWIFT / BIC code <span className="normal-case tracking-normal text-[#7a8d80] font-normal">— optional, for international wire payouts</span>
            </Label>
            <Input
              id="b_swift" type="text"
              autoComplete="off"
              value={form.swift_code}
              onChange={(e) => setForm(f => ({ ...f, swift_code: e.target.value.toUpperCase() }))}
              placeholder="e.g. CHASUS33 or CHASUS33XXX"
              maxLength={11}
              disabled={busy}
              className="uppercase tracking-widest"
              data-testid="bank-info-swift-input"
            />
            <p className="text-[10px] text-[#7a8d80]">8 or 11 letters/digits. Leave blank if you only need US ACH payouts.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="b_iban" className="text-xs uppercase tracking-widest text-[#4A5C50]">
              IBAN <span className="normal-case tracking-normal text-[#7a8d80] font-normal">— optional, for European bank accounts</span>
            </Label>
            <Input
              id="b_iban" type="text"
              autoComplete="off"
              value={form.iban}
              onChange={(e) => setForm(f => ({ ...f, iban: e.target.value.toUpperCase() }))}
              placeholder="e.g. GB82 WEST 1234 5698 7654 32"
              maxLength={42}
              disabled={busy}
              className="uppercase tracking-wider"
              data-testid="bank-info-iban-input"
            />
            <p className="text-[10px] text-[#7a8d80]">
              {isEdit && onFile.iban_last4 ? (
                <>Currently on file: <span className="mono">{onFile.iban_country} •••• {onFile.iban_last4}</span>. Re-type to keep, leave blank to clear, or enter a new IBAN to replace.</>
              ) : (
                <>15-34 chars: country code + check digits + bank account (e.g. GB, DE, FR). Leave blank if you only need ACH or SWIFT.</>
              )}
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="b_acct" className="text-xs uppercase tracking-widest text-[#4A5C50]">
              Account number
            </Label>
            <Input
              id="b_acct" type="text" required
              inputMode="numeric"
              autoComplete="off"
              value={form.bank_account_number}
              onChange={update("bank_account_number")}
              placeholder="Your bank account number"
              disabled={busy}
              data-testid="bank-info-account-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="b_acct2" className="text-xs uppercase tracking-widest text-[#4A5C50]">
              Confirm account number
            </Label>
            <Input
              id="b_acct2" type="text" required
              inputMode="numeric"
              autoComplete="off"
              value={form.confirm_account}
              onChange={update("confirm_account")}
              placeholder="Re-type to confirm"
              disabled={busy}
              data-testid="bank-info-account-confirm-input"
            />
            {form.confirm_account && form.confirm_account !== form.bank_account_number && (
              <p className="text-[11px] text-rose-700">Account numbers don&apos;t match.</p>
            )}
          </div>

          <div className="flex gap-2">
            {isEdit && (
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => onDismiss?.()}
                className="flex-1"
                data-testid="bank-info-cancel-btn"
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={busy}
              className={`${isEdit ? "flex-1" : "w-full"} bg-[#184A30] hover:bg-[#0F3320] text-white`}
              data-testid="bank-info-submit-btn"
            >
              {busy ? <><Spinner size={14} className="animate-spin mr-1" /> Saving…</> : <><ShieldCheck size={14} weight="fill" className="mr-1" /> {isEdit ? "Save changes" : "Save & unlock portal"}</>}
            </Button>
          </div>

          <p className="text-[10px] text-[#4A5C50] text-center">
            Your bank info is encrypted at rest and never shown to receivers or other agents.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
